import React from 'react';
import { SelectProps, SelectOption } from './types';
import SelectBase from '../SelectBase';
import SelectListPrompt from '../../Prompt/SelectListPrompt';
import PressableDynamicOpacity from '../../PressableDynamicOpacity';
import Stack from '../../Stack';
import Text from '../../Text';
import { getSpacing } from '../../../../utils/spacing';
import { StaticInputField } from '../InputBase';
import { View } from 'react-native';
import { isPresent } from 'ts-is-present';
import Chip from '../../Chip';
import _truncate from 'lodash/truncate';

export type { SelectOption, SelectProps };

const Select: React.FC<SelectProps> = (props) => {
    const getOption = (value: string | null) => props.selectOptions.find((option) => option.value === value);

    const handleSelect = (value: string | null) => {
        if (props.multiple && value !== null) {
            if ((props.value as string[] | null)?.includes(value)) {
                props.onChange((props.value as string[]).filter((val) => val !== value));
            }
        } else if (props.multiple) {
            props.onChange([...(props.value as string[]), value].filter(isPresent));
        } else {
            props.onChange(value);
        }
    };

    const renderInputValue = () => {
        if (props.multiple) {
            return (
                <View style={{ flexWrap: 'wrap', flexDirection: 'row', flex: 1, gap: 3 }}>
                    {(props.value as string[])
                        .map(getOption)
                        .filter(isPresent)
                        .map((option) => (
                            <View key={option.value}>
                                <Chip
                                    onDelete={() => {
                                        handleSelect(option.value);
                                    }}
                                    title={_truncate(option.label, { length: 30 })}
                                    highlight
                                />
                            </View>
                        ))}
                </View>
            );
        } else {
            return (
                <View style={{ flex: 1 }}>
                    <StaticInputField value={getOption(props.value)?.label || null} />
                </View>
            );
        }
    };

    const textDisplayValue = props.multiple
        ? (props.value as string[])
              .map(getOption)
              .filter(isPresent)
              .map((option) => option.label)
              .join(', ')
        : getOption(props.value)?.label;

    const inputValue = props.multiple
        ? props?.value?.length
            ? props?.value?.length.toString()
            : null
        : props?.value?.toString() || null;

    const getIsValueSelected = (value: string) => {
        if (props.multiple) {
            return (props.value as string[]).includes(value);
        } else {
            return props.value === value;
        }
    };

    return (
        <SelectBase
            {...props}
            onSelect={handleSelect}
            inputValue={inputValue}
            staticInputDisplay={textDisplayValue}
            renderInputValue={renderInputValue}
            renderMobileOptions={({ hasFocus, onCloseSelect, onOpenSelect }) =>
                props.multiple ? (
                    <SelectListPrompt
                        multiSelect
                        value={props.value || []}
                        options={props.selectOptions}
                        title={props.title}
                        onChange={(value) => {
                            props.onChange(value);
                        }}
                        isOpen={hasFocus}
                        setIsOpen={(isOpen) => {
                            isOpen ? onOpenSelect() : onCloseSelect();
                        }}
                    />
                ) : (
                    <SelectListPrompt
                        multiSelect={props.multiple}
                        value={props.value}
                        options={props.selectOptions}
                        title={props.title}
                        onChange={(value) => {
                            props.onChange(props.value === value ? null : value);
                            // delay for value to set.  this will call onblur.  Too early and you may still hit required validation errors with a value
                            setTimeout(() => onCloseSelect(), 50);
                        }}
                        isOpen={hasFocus}
                        setIsOpen={(isOpen) => {
                            isOpen ? onOpenSelect() : onCloseSelect();
                        }}
                    />
                )
            }
            renderDesktopOptions={({ onCloseSelect }) => (
                <>
                    {props.selectOptions.map((option) => (
                        <PressableDynamicOpacity
                            key={option.value}
                            onPress={() => {
                                if (props.multiple) {
                                    if ((props.value as string[]).includes(option.value)) {
                                        props.onChange((props.value as string[]).filter((val) => val !== option.value));
                                    } else {
                                        props.onChange([...(props.value as string[]), option.value]);
                                    }
                                } else {
                                    props.onChange(option.value === props.value ? null : option.value);
                                    // delay for value to set.  this will call onblur.  Too early and you may still hit required validation errors with a value
                                    setTimeout(() => onCloseSelect(), 50);
                                }
                            }}
                            style={{
                                width: '100%',
                                paddingTop: getSpacing(4),
                                paddingBottom: getSpacing(2),
                            }}
                            testID={`select-option-${option.testID || option.value}`}
                        >
                            <Stack
                                horizontal
                                justify="flex-start"
                                testID={
                                    getIsValueSelected(option.value)
                                        ? `selected-select-option-${option.value}`
                                        : `not-selected-select-option-${option.value}`
                                }
                            >
                                <Text
                                    variant={
                                        getIsValueSelected(option.value) ? 'selected-select-option' : 'select-option'
                                    }
                                >
                                    {option.label}
                                </Text>
                            </Stack>
                        </PressableDynamicOpacity>
                    ))}
                </>
            )}
        />
    );
};

export default React.memo(Select);
